<template>
  <nav aria-label="Main">
    <div class="nav-content">
      <div class="nav-links">
        <!-- Plain links: About Us, Services, Maturity Roadmap, Security Baseline, For Job Seekers -->
        <a
          v-for="link in primaryLinks"
          :key="link.href"
          :href="link.href"
          class="m400 header__link nav-title"
          :class="{ active: isCurrent(link.href) }"
          :aria-current="isCurrent(link.href) ? 'page' : undefined"
          @click.prevent="navigate(link.href)"
          >{{ link.title }}</a
        >

        <!--
          Legacy dropdowns, hidden in security mode (For Job Seekers, DIGU Cares, For Businesses,
          Our Solutions).
          Their routes stay registered. Items live in hiddenLegacyDropdowns in
          src/config/securityNavigation.js. To show them again, uncomment this block and add
          hiddenLegacyDropdowns to the securityNavigation import below.

        <v-menu
          v-for="menu in hiddenLegacyDropdowns"
          :key="menu.key"
          :model-value="openMenu === menu.key"
          :open-delay="0"
          :close-delay="300"
          location="bottom center"
          :transition="{ component: GsapDropdownTransition }"
          @update:model-value="(open) => setMenu(menu.key, open)"
        >
          <template v-slot:activator="{ props }">
            <p class="m400 nav-title" :class="{ active: openMenu === menu.key }" role="button" v-bind="props">
              {{ menu.title }}
              <v-icon class="dropdown-icon">mdi-chevron-down</v-icon>
            </p>
          </template>
          <v-list class="dropdown-menu" elevation="1" @mouseleave="closeAllMenus">
            <v-list-item>
              <div :class="{ 'solutions-grid': menu.grid }">
                <div
                  class="dropdown-content"
                  v-for="item in menu.items"
                  :key="item.href"
                  @click="navigate(item.href)"
                >
                  <div class="dropdown-title">{{ item.title }}</div>
                  <div class="dropdown-description">{{ item.description }}</div>
                </div>
              </div>
            </v-list-item>
          </v-list>
        </v-menu>
        -->

        <!-- The one dropdown kept from the legacy navbar: Resources -->
        <v-menu
          v-for="menu in dropdowns"
          :key="menu.key"
          :model-value="openMenu === menu.key"
          :open-delay="0"
          :close-delay="300"
          location="bottom center"
          :transition="{ component: GsapDropdownTransition, y: -14, scale: 0.96, blur: 8, duration: 0.6 }"
          @update:model-value="(open) => setMenu(menu.key, open)"
        >
          <template v-slot:activator="{ props }">
            <p class="m400 nav-title" :class="{ active: openMenu === menu.key }" role="button" v-bind="props">
              {{ menu.title }}
              <v-icon class="dropdown-icon" :class="{ 'dropdown-icon--open': openMenu === menu.key }">
                mdi-chevron-down
              </v-icon>
            </p>
          </template>
          <!-- Mega panel: icon tiles in a grid, the featured item (Learning Hub) as a picture card.
               Elements with data-menu-stagger animate in and out one after another (GSAP). -->
          <div class="res-menu" @mouseleave="closeAllMenus">
            <ul class="res-menu__grid">
              <li v-for="item in gridItems(menu)" :key="item.href" data-menu-stagger>
                <a class="res-item" :href="item.href" @click.prevent="navigate(item.href)">
                  <span class="res-item__icon" aria-hidden="true" data-menu-pop>
                    <v-icon size="22">{{ item.icon }}</v-icon>
                  </span>
                  <span class="res-item__copy">
                    <span class="m600 res-item__title">{{ item.title }}</span>
                    <span class="m400 res-item__desc">{{ item.description }}</span>
                  </span>
                </a>
              </li>
            </ul>
            <a
              v-for="item in featuredItems(menu)"
              :key="item.href"
              class="res-feature"
              :href="item.href"
              data-menu-stagger
              @click.prevent="navigate(item.href)"
            >
              <span class="res-feature__media">
                <img :src="item.image" alt="" width="741" height="252" />
              </span>
              <span class="res-feature__body">
                <span class="o600 res-feature__title">
                  <v-icon size="20" aria-hidden="true">{{ item.icon }}</v-icon>
                  {{ item.title }}
                </span>
                <span class="m400 res-feature__desc">{{ item.description }}</span>
                <v-icon class="res-feature__arrow" size="20" aria-hidden="true">mdi-arrow-right</v-icon>
              </span>
            </a>
          </div>
        </v-menu>
      </div>

      <!-- Replaced by "Score yourself" in security mode.
      <v-btn @click="redirectToCalendly" class="contact-btn">
        Schedule a Meeting
      </v-btn>
      -->
      <v-btn class="contact-btn" :href="primaryCta.href" @click.prevent="navigate(primaryCta.href)">
        {{ primaryCta.title }}
      </v-btn>
    </div>

    <!-- Overlay Background -->
    <gsap-dropdown-transition :y="0" :duration="0.4">
      <div
        v-if="openMenu"
        class="backdrop-overlay"
        :style="{ top: backdropTop + 'px' }"
        @click="closeAllMenus"
      ></div>
    </gsap-dropdown-transition>
  </nav>
</template>

<script setup>
// Security-mode copy of partials/DesktopMenu.vue. Same classes and styles, with items
// from src/config/securityNavigation.js. Dropdowns open and close through GSAP
// (GsapDropdownTransition) and navigation scrolls to the top with GSAP ScrollTo.
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import GsapDropdownTransition from './GsapDropdownTransition';
import { useSmoothNavigate } from '@/composables/security/useSmoothScroll';
import { primaryLinks, primaryCta, dropdowns } from '@/config/securityNavigation';

const emit = defineEmits(['menu-state-change']);

const route = useRoute();
const smoothNavigate = useSmoothNavigate();
const openMenu = ref(null);
const backdropTop = ref(0);

// Exact match for "/", prefix match otherwise so detail pages keep their section lit.
const isCurrent = (href) => (href === '/' ? route.path === '/' : route.path === href || route.path.startsWith(`${href}/`));

function setMenu(key, open) {
  if (open) openMenu.value = key;
  else if (openMenu.value === key) openMenu.value = null;
}

// Resources panel: the featured item becomes the picture card, the rest fill the grid.
const gridItems = (menu) => menu.items.filter((item) => !item.featured);
const featuredItems = (menu) => menu.items.filter((item) => item.featured);

function closeAllMenus() {
  openMenu.value = null;
}

function navigate(path) {
  closeAllMenus();
  smoothNavigate(path);
}

watch(openMenu, (key) => {
  const isOpen = Boolean(key);
  if (isOpen) {
    // The backdrop starts where the header ends, whatever its current (sticky) height.
    const header = document.querySelector('header.header--global');
    backdropTop.value = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
  }
  emit('menu-state-change', isOpen);
  document.body.classList.toggle('menu-open', isOpen);
});
</script>

<style scoped>
nav {
  flex: 1;
  display: none;
  background-color: var(--sec-ground);
  z-index: 1001;
  width: 100%;
}

.nav-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0 auto;
}

/* Desktop nav from 1282px, same as the legacy header; below 1440px the 18px links pack a
   little tighter so they fit beside the logo and button. */
@media only screen and (min-width: 1282px) {
  nav {
    display: block;
  }
}

@media only screen and (min-width: 1282px) and (max-width: 1439px) {
  .nav-links {
    gap: 0;
  }
  .nav-title {
    padding-inline: 0.4rem;
  }
}

.nav-title {
  display: inline-flex;
  align-items: center;
  margin: 0;
  color: var(--sec-ink);
  font-size: 18px;
  line-height: 1.2;
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 0.625rem;
  cursor: pointer;
  transition: color 0.2s ease;
  white-space: nowrap;
}

.nav-title:hover,
.nav-title.active {
  color: var(--sec-accent);
}

.dropdown-menu {
  border-radius: 0.75rem;
  margin-top: 1.25rem;
  min-width: 250px;
  background-color: var(--sec-surface);
  z-index: 1001;
  cursor: pointer;
}

.solutions-grid {
  display: grid;
  grid-template-columns: repeat(2, 2fr);
}

.dropdown-content {
  display: flex;
  flex-direction: column;
  height: 55px;
  overflow: hidden;
  padding: 0.3rem 1rem;
}

.dropdown-content:hover {
  background-color: var(--sec-ground);
}

.dropdown-title {
  color: var(--sec-ink);
  font-size: 16px;
  text-align: left;
  z-index: 1;
}

.dropdown-content:hover .dropdown-title {
  color: var(--sec-accent);
}

.dropdown-description {
  font-size: 0.875rem;
  color: var(--sec-ink-2);
  line-height: 1.4;
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  text-align: left;
  z-index: 1;
  transition: opacity 0.2s ease, max-height 0.2s ease;
}

.dropdown-content:hover .dropdown-description {
  opacity: 1;
  max-height: 60px;
}

.v-list-item {
  border: none !important;
  outline: none !important;
  padding: 0;
}

.v-list-item:hover {
  background: none !important;
}

/* Compact CTA: sized to its label with a little padding. */
.contact-btn {
  flex-shrink: 0;
  text-transform: none;
  letter-spacing: 0.01em;
  font-size: 18px;
  font-weight: 500;
  background: var(--sec-accent-strong);
  height: 44px !important;
  min-width: 0;
  padding-inline: 16px !important;
  color: var(--sec-on-accent);
  border-radius: 8px;
}

.contact-btn:hover {
  background-color: var(--sec-accent);
  color: var(--sec-on-accent);
}

.backdrop-overlay {
  position: fixed;
  top: 0; /* overridden inline with the header's bottom edge */
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--sec-backdrop);
  z-index: 1000;
}

/* Resources mega panel */
.res-menu {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: var(--sec-s2);
  width: min(780px, calc(100vw - 2rem));
  margin-top: 1.25rem;
  padding: var(--sec-s2);
  background: var(--sec-surface);
  border: 1px solid var(--sec-line);
  border-radius: 16px;
  box-shadow: 0 24px 48px -16px var(--sec-header-shadow), 0 8px 16px -8px var(--sec-header-shadow);
}

.res-menu__grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  align-content: start;
}

/* An odd tile out (5 items in 2 columns) spans the full row, so the grid stays even. */
.res-menu__grid > li:last-child:nth-child(odd) {
  grid-column: 1 / -1;
}

.res-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  height: 100%;
  padding: 14px;
  border-radius: 12px;
  text-decoration: none;
  transition: background-color 0.2s ease;
}

.res-item:hover,
.res-item:focus-visible {
  background: var(--sec-ground);
}

.res-item__icon {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: var(--sec-accent-subtle);
  color: var(--sec-accent-text);
  transition: background-color 0.2s ease, color 0.2s ease;
}

.res-item:hover .res-item__icon,
.res-item:focus-visible .res-item__icon {
  background: var(--sec-accent-strong);
  color: var(--sec-on-accent);
}

.res-item__copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  text-align: left;
}

.res-item__title {
  font-size: 15px;
  line-height: 1.3;
  color: var(--sec-ink);
  transition: color 0.2s ease;
}

.res-item:hover .res-item__title,
.res-item:focus-visible .res-item__title {
  color: var(--sec-accent-text);
}

.res-item__desc {
  font-size: 13px;
  line-height: 1.45;
  color: var(--sec-ink-2);
}

.res-feature {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: var(--sec-accent-subtle);
  text-decoration: none;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.res-feature:hover,
.res-feature:focus-visible {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px -12px var(--sec-header-shadow);
}

/* The picture area takes any extra height (a taller grid beside it), keeping the image
   centred and the text anchored at the bottom of the card. */
.res-feature__media {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 12px 12px 0;
}

.res-feature__media img {
  display: block;
  width: 100%;
  height: auto;
}

.res-feature__body {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px 16px;
  text-align: left;
}

.res-feature__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 17px;
  color: var(--sec-ink);
}

.res-feature__title .v-icon {
  color: var(--sec-accent-text);
}

.res-feature__desc {
  padding-right: 28px;
  font-size: 13px;
  line-height: 1.45;
  color: var(--sec-ink-2);
}

.res-feature__arrow {
  position: absolute;
  right: 16px;
  bottom: 16px;
  color: var(--sec-accent-text);
  transition: transform 0.2s ease;
}

.res-feature:hover .res-feature__arrow {
  transform: translateX(3px);
}

@media (prefers-reduced-motion: reduce) {
  .res-feature,
  .res-feature__arrow {
    transition: none;
  }
  .res-feature:hover {
    transform: none;
  }
}

.dropdown-icon {
  color: var(--sec-icon);
  font-size: 20px;
  margin-left: 2px;
  transition: transform 0.25s ease;
}

.dropdown-icon--open {
  transform: rotate(180deg);
}
</style>
